import { useState } from 'react';
import { Download, Upload, Database, AlertCircle, CheckCircle2 } from 'lucide-react';
import { api } from '../services/api';
import Layout from '../components/Layout';

function showToast(type: 'success' | 'error' | 'info', message: string) {
  window.dispatchEvent(
    new CustomEvent('toast', {
      detail: { type, message },
    })
  );
}

export default function BackupPage() {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const response = await api.get('/backup/export');
      
      // Criar arquivo JSON e fazer download
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showToast('success', 'Backup exportado com sucesso!');
    } catch (error: any) {
      showToast('error', error.response?.data?.error || 'Erro ao exportar backup');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsImporting(true);
      const fileContent = await file.text();
      const backupData = JSON.parse(fileContent);
      
      await api.post('/backup/import', backupData);
      
      showToast('success', 'Backup importado com sucesso! A página será recarregada.');
      
      // Recarregar página após 2 segundos
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      showToast('error', error.response?.data?.error || 'Erro ao importar backup');
    } finally {
      setIsImporting(false);
      // Limpar input
      event.target.value = '';
    }
  };

  return (
    <Layout>
      <div className="flex flex-col h-screen bg-gray-100">
        <div className="bg-white border-b border-gray-200 px-4 md:px-8 py-6 flex-shrink-0">
          <div className="flex items-center gap-3 pb-2">
            <Database className="w-7 h-7 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Backup & Restauração</h1>
          </div>
          <p className="text-gray-600 text-sm">Exporte ou importe seus dados financeiros de forma segura</p>
        </div>

        <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
          {/* Alerta de Segurança */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3 mb-6">
            <Database className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">Segurança dos Dados</h3>
              <p className="text-sm text-blue-700">
                Seus dados são isolados e apenas você tem acesso. Guarde os backups em local seguro 
                (Google Drive, Dropbox, etc).
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Exportar Backup */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Download className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Exportar Dados</h2>
                  <p className="text-sm text-gray-500">Baixar backup em JSON</p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <p className="text-sm text-gray-700 font-medium">O que será exportado:</p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Todas as transações
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Fontes de renda
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Parcelas de cartão
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Contas recorrentes
                  </li>
                </ul>
              </div>

              <button
                onClick={handleExport}
                disabled={isExporting}
                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    Exportando...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Exportar Backup
                  </>
                )}
              </button>
            </div>

            {/* Importar Backup */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Upload className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Importar Dados</h2>
                  <p className="text-sm text-gray-500">Restaurar de backup</p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-red-900 text-sm mb-1">Atenção!</h3>
                    <p className="text-xs text-red-700">
                      A importação adiciona os dados do backup aos existentes. Não remove dados atuais.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <label className="block">
                  <div className="w-full px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors flex items-center justify-center gap-2 cursor-pointer">
                    {isImporting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                        Importando...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        Clique para selecionar arquivo
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImport}
                    disabled={isImporting}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 text-center">Arquivo JSON de backup</p>
              </div>
            </div>
          </div>

          {/* Como Usar */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4">Como Usar</h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <Download className="w-4 h-4 text-blue-600" />
                  Para fazer backup:
                </h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 ml-6">
                  <li>Clique em "Exportar Backup"</li>
                  <li>Salve o arquivo JSON baixado em local seguro</li>
                  <li>Recomendamos guardar no Google Drive, Dropbox ou similar</li>
                  <li>Faça backups regularmente (ex: mensalmente)</li>
                </ol>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <Upload className="w-4 h-4 text-blue-600" />
                  Para restaurar backup:
                </h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 ml-6">
                  <li>Clique na área de upload ou arraste o arquivo</li>
                  <li>Selecione o arquivo JSON de backup previamente exportado</li>
                  <li>Aguarde a importação ser concluída</li>
                  <li>A página será recarregada automaticamente</li>
                </ol>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                <p className="text-xs text-yellow-800">
                  <strong>Dica:</strong> Configure uma rotina de backup mensal. Você também pode fazer upload dos backups 
                  em serviços de nuvem automaticamente usando ferramentas do Google Drive, Dropbox, etc.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
